import React, { useEffect } from 'react';
import { FormControl, InputLabel, Select, MenuItem, FormHelperText } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { AnalyticsPeriod } from '../../../models/enums/analyticsPeriod';
import { AnalyticsInterval, AnalyticsIntervalUtil } from '../../../models/enums/analyticsInterval';

interface IntervalFormControlProps {
    name: string;
    periodName: string; // Name of the period field to watch
    label?: string;
    required?: boolean;
    disabled?: boolean;
}

export const IntervalFormControl: React.FC<IntervalFormControlProps> = ({
    name,
    periodName,
    label = 'Интервал',
    required = true,
    disabled = false
}) => {
    const { control, watch, setValue } = useFormContext();
    const period = watch(periodName) as AnalyticsPeriod;
    const interval = watch(name) as AnalyticsInterval;

    const getAvailableIntervals = () => {
        if (period === AnalyticsPeriod.Day) {
            return [
                { value: AnalyticsInterval.FifteenMinutes, label: AnalyticsIntervalUtil.getStringValue(AnalyticsInterval.FifteenMinutes) },
                { value: AnalyticsInterval.Hour, label: AnalyticsIntervalUtil.getStringValue(AnalyticsInterval.Hour) },
                { value: AnalyticsInterval.Day, label: AnalyticsIntervalUtil.getStringValue(AnalyticsInterval.Day) }
            ];
        } else if (period === AnalyticsPeriod.Month) {
            return [
                { value: AnalyticsInterval.Day, label: AnalyticsIntervalUtil.getStringValue(AnalyticsInterval.Day) }
            ];
        }
        return [];
    };

    // Auto-reset interval when period changes
    useEffect(() => {
        if (period === AnalyticsPeriod.Day) {
            // Available intervals: 15 минут, Час, День
            if (interval && ![AnalyticsInterval.FifteenMinutes, AnalyticsInterval.Hour, AnalyticsInterval.Day].includes(interval)) {
                setValue(name, '' as AnalyticsInterval | '');
            }
        } else if (period === AnalyticsPeriod.Month) {
            // Available intervals: День
            if (interval && interval !== AnalyticsInterval.Day) {
                setValue(name, AnalyticsInterval.Day);
            }
        }
    }, [period, interval, name, setValue]);

    const availableIntervals = getAvailableIntervals();
    const isDisabled = disabled || !period;

    return (
        <Controller
            name={name}
            control={control}
            rules={{ required: required ? 'Обязательное поле' : false }}
            render={({ field, fieldState }) => (
                <FormControl fullWidth size="small" error={!!fieldState.error} disabled={isDisabled} required={required}>
                    <InputLabel 
                        required={required}
                        sx={{ 
                            whiteSpace: 'nowrap',
                            overflow: 'visible',
                            textOverflow: 'clip',
                            maxWidth: 'none'
                        }}
                    >
                        {label}
                    </InputLabel>
                    <Select
                        {...field}
                        label={label}
                        disabled={isDisabled}
                        required={required}
                        renderValue={(value) => AnalyticsIntervalUtil.getStringValue(value as AnalyticsInterval)}
                    >
                        {availableIntervals.map(interval => (
                            <MenuItem key={interval.value} value={interval.value}>
                                {interval.label}
                            </MenuItem>
                        ))}
                    </Select>
                    {fieldState.error && (
                        <FormHelperText>{fieldState.error.message}</FormHelperText>
                    )}
                </FormControl>
            )}
        />
    );
};

